import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { peopleKeys } from '../people/keys.ts'
import { tagKeys } from './keys.ts'

/**
 * Tag hooks. Tags are a bounded list (tens per church), so the org-wide list
 * is a plain query the tag picker can filter in memory rather than an infinite
 * one — the API still pages, and `limit=200` covers every real church.
 */

export function useTags(personId?: string) {
  const api = useApi()
  return useQuery({
    queryKey: tagKeys.list(personId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.tags.get({
        query: { limit: 200, ...(personId ? { personId } : {}) },
      })
      if (error) throw error
      return data.items
    },
  })
}

export type Tag = NonNullable<ReturnType<typeof useTags>['data']>[number]

/** Any tag change moves person counts and directory membership. */
function useTagInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: tagKeys.all() })
    void queryClient.invalidateQueries({ queryKey: peopleKeys.lists() })
  }
}

export function useCreateTag() {
  const api = useApi()
  const invalidate = useTagInvalidation()
  return useMutation({
    mutationFn: async (body: Parameters<typeof api.api.v1.tags.post>[0]) => {
      const { data, error } = await api.api.v1.tags.post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUpdateTag(tagId: string) {
  const api = useApi()
  const invalidate = useTagInvalidation()
  return useMutation({
    mutationFn: async (body: Parameters<ReturnType<typeof api.api.v1.tags>['patch']>[0]) => {
      const { data, error } = await api.api.v1.tags({ id: tagId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useDeleteTag() {
  const api = useApi()
  const invalidate = useTagInvalidation()
  return useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await api.api.v1.tags({ id: tagId }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })
}

/** Apply/remove for one person or a whole selection — the same endpoint. */
export function useApplyTag() {
  const api = useApi()
  const invalidate = useTagInvalidation()
  return useMutation({
    mutationFn: async ({ tagId, personIds }: { tagId: string; personIds: string[] }) => {
      const { data, error } = await api.api.v1.tags({ id: tagId }).apply.post({ personIds })
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useRemoveTag() {
  const api = useApi()
  const invalidate = useTagInvalidation()
  return useMutation({
    mutationFn: async ({ tagId, personIds }: { tagId: string; personIds: string[] }) => {
      const { data, error } = await api.api.v1.tags({ id: tagId }).remove.post({ personIds })
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}
